import type { InitOtelOptions } from "@/lib/otel";
// Only dependency-free modules may be imported statically here: anything
// that reaches `@opentelemetry/*` would put the SDK (and zone.js, which
// patches globals on load) back into the main chunk.
import {
  OTEL_SESSION_STORAGE_KEY,
  getAnalyticsConsent,
  subscribeToConsent,
} from "@/lib/consent";

/**
 * Consent gate for the browser OTel SDK (ADR-051 §5).
 *
 * `lib/otel.ts` and the whole OpenTelemetry stack (zone.js included) are
 * loaded through a dynamic `import()` only once analytics consent is
 * granted, so a visitor who hasn't chosen — or declined — never downloads
 * or runs any of it: no device-info reads, no `sessionStorage` session id,
 * no Cloudflare HEAD probe, no instrumentation, no export.
 *
 * Every consent change (this tab or another) re-applies the current state:
 * granted → `initOtel()`, anything else → `shutdownOtel()`. Changes are
 * serialised so a rapid accept/withdraw sequence can't interleave.
 */

type OtelModule = typeof import("@/lib/otel");

let otelModule: Promise<OtelModule> | null = null;
let queue: Promise<void> = Promise.resolve();
let stopSync: (() => void) | null = null;

function loadOtel(): Promise<OtelModule> {
  otelModule ??= import("@/lib/otel");
  return otelModule;
}

function clearSessionId(): void {
  try {
    sessionStorage.removeItem(OTEL_SESSION_STORAGE_KEY);
  } catch {
    // Storage disabled -> nothing was stored.
  }
}

/**
 * Starts or stops the SDK to match the recorded consent. Resolves once the
 * change is applied (provider flush on shutdown is not awaited).
 */
export function applyAnalyticsConsent(
  options: InitOtelOptions = {},
): Promise<void> {
  queue = queue
    .then(async () => {
      if (getAnalyticsConsent() === "granted") {
        const otel = await loadOtel();
        // Consent may have been withdrawn while the chunk was loading.
        if (getAnalyticsConsent() !== "granted") return;
        otel.initOtel(options);
        return;
      }
      // Also removes a session id left in this tab by an earlier,
      // ungated build of the dashboard.
      clearSessionId();
      if (otelModule) {
        const otel = await otelModule;
        void otel.shutdownOtel();
      }
    })
    .catch(() => {
      // Chunk load failure: analytics stay off; the next consent change
      // retries the import.
      otelModule = null;
    });
  return queue;
}

/**
 * Applies the current consent and keeps the SDK in sync with later changes
 * (banner, Settings, other tabs). Returns a stop function (tests only).
 */
export function startAnalyticsConsentSync(
  options: InitOtelOptions = {},
): () => void {
  stopSync?.();
  void applyAnalyticsConsent(options);
  const unsubscribe = subscribeToConsent(() => {
    void applyAnalyticsConsent(options);
  });
  stopSync = () => {
    unsubscribe();
    stopSync = null;
  };
  return stopSync;
}
